/**
 * /app/combos — Gestión de combos (Admin UI).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudEmptyState, CrudShellHeader, DataTable, Loading } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Package, Pencil, Plus, Trash2, X, ChevronUp, ChevronDown } from "lucide-react";
import { ImageUploadField } from "@/components/ImageUploadField";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import { CrudEditorShell } from "@/components/workspace/CrudEditorShell";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";

export const Route = createFileRoute("/app/combos")({
  component: CombosPage,
  head: () => ({ meta: [{ title: "Combos — Structura Market" }] }),
});

// ─── Types ────────────────────────────────────────────────────────────────────

interface ComboItem {
  id: string;
  comboId: string;
  productId: string;
  quantity: number;
  sortOrder: number;
}

interface Combo {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  images: string[];
  price: number;
  discountPct: number | null;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  items: ComboItem[];
}

interface ProductHit {
  id: string;
  name: string;
  imagenUrl: string | null;
  precioUnitario: number | null;
  slug: string;
}

/**
 * GET /api/products con `limit` devuelve `{ items, total }` (paginado) en vez
 * de un array plano — sin este unwrap, `searchResults` queda como un objeto y
 * `.length` da `undefined`, así que el dropdown de resultados nunca se renderiza.
 */
function unwrapProductHits(raw: ProductHit[] | { items: ProductHit[] }): ProductHit[] {
  return Array.isArray(raw) ? raw : raw.items;
}

// ─── Builder item ─────────────────────────────────────────────────────────────

interface BuilderItem {
  productId: string;
  productName: string;
  productImagenUrl: string | null;
  precioUnitario: number | null;
  quantity: number;
  sortOrder: number;
}

const EMPTY_FORM = {
  name: "",
  description: "",
  images: [] as string[],
  price: 0,
  discountPct: "" as string | number,
  priceMode: "fixed" as "fixed" | "discount",
  sortOrder: 0,
};

function fmt(n: number) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

// ─── Main page ────────────────────────────────────────────────────────────────

function CombosPage() {
  const tid = useWorkspaceTenantId();
  const [combos, setCombos] = useState<Combo[]>([]);
  const [loading, setLoading] = useState(true);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Combo | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [builderItems, setBuilderItems] = useState<BuilderItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Combo | null>(null);

  // Product search
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<ProductHit[]>([]);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    if (!tid) return;
    setLoading(true);
    setLock(null);
    apiGetJson<Combo[]>("/api/combos", tid)
      .then(setCombos)
      .catch((e: unknown) => {
        const err = e as Error & { planLock?: PlanApiErrorPayload };
        if (err.planLock) setLock(err.planLock);
        else toast.error("Error al cargar combos");
      })
      .finally(() => setLoading(false));
  }, [tid]);

  useEffect(load, [load]);

  // ── Search products ──
  useEffect(() => {
    if (!tid || !searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const raw = await apiGetJson<ProductHit[] | { items: ProductHit[] }>(
          `/api/products?q=${encodeURIComponent(searchQ)}&limit=20`,
          tid,
        );
        setSearchResults(unwrapProductHits(raw));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => { if (searchRef.current) clearTimeout(searchRef.current); };
  }, [searchQ, tid]);

  // ── Derived: suma de precios ──
  const priceSum = builderItems.reduce((s, it) => s + (it.precioUnitario ?? 0) * it.quantity, 0);

  const calcFinalPrice = () => {
    if (form.priceMode === "discount") {
      const pct = Number(form.discountPct) || 0;
      return priceSum * (1 - pct / 100);
    }
    return form.price;
  };

  // ── Open dialog ──
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setBuilderItems([]);
    setSearchQ("");
    setSearchResults([]);
    setEditorOpen(true);
  };

  const openEdit = async (combo: Combo) => {
    setEditing(combo);
    setForm({
      name: combo.name,
      description: combo.description ?? "",
      images: combo.images ?? [],
      price: combo.price,
      discountPct: combo.discountPct ?? "",
      priceMode: combo.discountPct != null ? "discount" : "fixed",
      sortOrder: combo.sortOrder,
    });
    // Load product names for builder items
    if (!tid) return;
    const items: BuilderItem[] = [];
    for (const ci of combo.items) {
      try {
        const raw = await apiGetJson<ProductHit[] | { items: ProductHit[] }>(
          `/api/products?q=&limit=1&id=${ci.productId}`,
          tid,
        ).catch(() => []);
        const hit = unwrapProductHits(raw)[0];
        items.push({
          productId: ci.productId,
          productName: hit?.name ?? ci.productId,
          productImagenUrl: hit?.imagenUrl ?? null,
          precioUnitario: hit?.precioUnitario ?? null,
          quantity: ci.quantity,
          sortOrder: ci.sortOrder,
        });
      } catch {
        items.push({
          productId: ci.productId,
          productName: ci.productId,
          productImagenUrl: null,
          precioUnitario: null,
          quantity: ci.quantity,
          sortOrder: ci.sortOrder,
        });
      }
    }
    setBuilderItems(items);
    setSearchQ("");
    setSearchResults([]);
    setEditorOpen(true);
  };

  // ── Add product to builder ──
  const addProduct = (hit: ProductHit) => {
    const already = builderItems.some((it) => it.productId === hit.id);
    if (already) {
      toast.info(`${hit.name} ya está en el combo`);
      return;
    }
    setBuilderItems((prev) => [
      ...prev,
      {
        productId: hit.id,
        productName: hit.name,
        productImagenUrl: hit.imagenUrl,
        precioUnitario: hit.precioUnitario,
        quantity: 1,
        sortOrder: prev.length,
      },
    ]);
    setSearchQ("");
    setSearchResults([]);
  };

  const removeItem = (productId: string) =>
    setBuilderItems((prev) => prev.filter((it) => it.productId !== productId));

  const setItemQty = (productId: string, qty: number) =>
    setBuilderItems((prev) =>
      prev.map((it) => (it.productId === productId ? { ...it, quantity: Math.max(1, qty) } : it)),
    );

  const moveItem = (productId: string, dir: -1 | 1) => {
    setBuilderItems((prev) => {
      const idx = prev.findIndex((it) => it.productId === productId);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr.map((it, i) => ({ ...it, sortOrder: i }));
    });
  };

  // ── Save ──
  const handleSave = async () => {
    if (!tid) return;
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    if (builderItems.length === 0) { toast.error("Agregá al menos un producto al combo"); return; }
    const finalPrice = calcFinalPrice();

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        images: form.images,
        price: finalPrice,
        discountPct: form.priceMode === "discount" ? (Number(form.discountPct) || null) : null,
        sortOrder: form.sortOrder,
        items: builderItems.map((it, i) => ({
          productId: it.productId,
          quantity: it.quantity,
          sortOrder: i,
        })),
      };

      if (editing) {
        await apiSendJson(`/api/combos/${editing.id}`, "PATCH", payload, tid);
        toast.success("Combo actualizado");
      } else {
        await apiSendJson("/api/combos", "POST", payload, tid);
        toast.success("Combo creado");
      }
      setEditorOpen(false);
      load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (combo: Combo) => {
    if (!tid) return;
    try {
      await apiSendJson(`/api/combos/${combo.id}`, "PATCH", { active: !combo.active }, tid);
      setCombos((prev) => prev.map((c) => (c.id === combo.id ? { ...c, active: !c.active } : c)));
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !tid) return;
    try {
      await apiDelete(`/api/combos/${deleteTarget.id}`, tid);
      toast.success("Combo eliminado");
      setCombos((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  // ── Render ──
  const columns: DataTableColumn<Combo>[] = [
    { key: "name", header: "Nombre", className: "font-medium", render: (c) => c.name },
    {
      key: "items",
      header: "Items",
      className: "text-muted-foreground",
      render: (c) => `${c.items.length} producto${c.items.length !== 1 ? "s" : ""}`,
    },
    { key: "price", header: "Precio", className: "font-mono", render: (c) => fmt(c.price) },
    {
      key: "active",
      header: "Activo",
      className: "text-center",
      render: (c) => (
        <div className="flex justify-center">
          <Switch checked={c.active} onCheckedChange={() => void handleToggleActive(c)} />
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (c) => (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => void openEdit(c)} title="Editar">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setDeleteTarget(c)}
            title="Eliminar"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (lock) return <FeatureLockedState payload={lock} />;

  return (
    <div>
      <CrudShellHeader
        title="Combos"
        description="Agrupá productos existentes. Podés poner un precio propio o un descuento."
        count={combos.length}
        backHref="/app/catalogacion"
        backLabel="Volver a Catalogación"
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Nuevo combo
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="pt-6">
        <CrudEditorShell
          open={editorOpen}
          onOpenChange={(open) => {
            if (saving) return;
            setEditorOpen(open);
            if (!open) setEditing(null);
          }}
          title={editing ? "Editar combo" : "Nuevo combo"}
          size="lg"
          list={
            <>
              <p className="mb-3 text-sm text-muted-foreground">
                Agrupá productos existentes en un combo con precio fijo o descuento.
              </p>
              {loading ? (
          <Loading variant="list" />
        ) : (
          <DataTable
            columns={columns}
            rows={combos}
            getRowKey={(c) => c.id}
            onRowClick={(c) => void openEdit(c)}
            emptyState={
              <CrudEmptyState
                icon={<Package className="h-8 w-8" />}
                title="No hay combos creados aún"
                description="Agrupá dos o más productos con un precio combinado y ofrecelos en el catálogo."
                action={
                  <Button size="sm" onClick={openCreate}>
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Primer combo
                  </Button>
                }
              />
            }
          />
        )}
            </>
          }
          footer={
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setEditorOpen(false);
                  setEditing(null);
                }}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button className="flex-1" onClick={() => void handleSave()} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? "Guardar cambios" : "Crear combo"}
              </Button>
            </>
          }
        >
          <div className="grid gap-5 py-2">
            {/* Nombre */}
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="combo-name" tipKey="combo.name" required>Nombre</FieldLabel>
              <Input
                id="combo-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ej: Combo Familiar"
                disabled={saving}
              />
            </div>

            {/* Descripción */}
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="combo-desc" tipKey="combo.description">Descripción</FieldLabel>
              <Input
                id="combo-desc"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descripción opcional del combo"
                disabled={saving}
              />
            </div>

            {/* Imágenes */}
            <div className="grid gap-1.5">
              <FieldLabel tipKey="combo.images">Imágenes</FieldLabel>
              <p className="text-xs text-muted-foreground">Hasta 5 fotos. Subí un archivo o elegí de la biblioteca.</p>
              <div className="space-y-3">
                {form.images.filter(Boolean).map((url, i) => (
                  <ImageUploadField
                    key={`${url}-${i}`}
                    kind="product"
                    value={url}
                    hideLabel
                    hideHint
                    tenantId={tid}
                    disabled={saving}
                    onChange={(next) => {
                      setForm((f) => {
                        const imgs = f.images.filter(Boolean);
                        if (!next) return { ...f, images: imgs.filter((_, j) => j !== i) };
                        imgs[i] = next;
                        return { ...f, images: imgs };
                      });
                    }}
                  />
                ))}
                {form.images.filter(Boolean).length < 5 ? (
                  <ImageUploadField
                    kind="product"
                    value={null}
                    hideLabel
                    hideHint
                    tenantId={tid}
                    disabled={saving}
                    onChange={(next) => {
                      if (!next) return;
                      setForm((f) => ({ ...f, images: [...f.images.filter(Boolean), next] }));
                    }}
                  />
                ) : null}
              </div>
            </div>

            {/* Buscador de productos */}
            <div className="grid gap-1.5">
              <FieldLabel tipKey="combo.items">Agregar productos al combo</FieldLabel>
              <div className="relative">
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Buscar producto por nombre..."
                  disabled={saving}
                />
                {(searching || searchResults.length > 0) && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
                    {searching && (
                      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Buscando...
                      </div>
                    )}
                    {!searching && searchResults.map((hit) => (
                      <button
                        key={hit.id}
                        type="button"
                        onClick={() => addProduct(hit)}
                        className="flex w-full items-center gap-3 p-3 text-left text-sm hover:bg-accent transition-colors"
                      >
                        {hit.imagenUrl ? (
                          <img src={hit.imagenUrl} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
                        ) : (
                          <div className="h-8 w-8 rounded bg-muted flex items-center justify-center shrink-0">
                            <Package className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="flex-1 truncate font-medium">{hit.name}</span>
                        {hit.precioUnitario != null && (
                          <span className="text-muted-foreground shrink-0">{fmt(hit.precioUnitario)}</span>
                        )}
                      </button>
                    ))}
                    {!searching && searchResults.length === 0 && searchQ.trim() && (
                      <div className="p-3 text-sm text-muted-foreground">Sin resultados</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Lista de items del combo */}
            {builderItems.length > 0 && (
              <div className="grid gap-1.5">
                <FieldLabel tipKey="combo.items">Productos del combo</FieldLabel>
                <div className="space-y-2">
                  {builderItems.map((it, idx) => (
                    <div key={it.productId} className="flex items-center gap-3 rounded-lg border p-3">
                      {it.productImagenUrl ? (
                        <img src={it.productImagenUrl} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{it.productName}</p>
                        {it.precioUnitario != null && (
                          <p className="text-xs text-muted-foreground">{fmt(it.precioUnitario)} c/u</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => moveItem(it.productId, -1)} disabled={idx === 0}>
                          <ChevronUp className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => moveItem(it.productId, 1)} disabled={idx === builderItems.length - 1}>
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </div>
                      <Input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) => setItemQty(it.productId, parseInt(e.target.value) || 1)}
                        className="w-16 text-center"
                        disabled={saving}
                      />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => removeItem(it.productId)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Precio */}
            <div className="grid gap-3 rounded-lg border p-4">
              <FieldLabel tipKey="combo.priceMode">Precio del combo</FieldLabel>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Suma de precios</span>
                <span className="font-mono font-semibold">{fmt(priceSum)}</span>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="priceMode"
                    value="fixed"
                    checked={form.priceMode === "fixed"}
                    onChange={() => setForm((f) => ({ ...f, priceMode: "fixed" }))}
                  />
                  <span className="text-sm">Precio fijo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="priceMode"
                    value="discount"
                    checked={form.priceMode === "discount"}
                    onChange={() => setForm((f) => ({ ...f, priceMode: "discount" }))}
                  />
                  <span className="text-sm">% Descuento</span>
                </label>
              </div>
              {form.priceMode === "fixed" ? (
                <div className="grid gap-1.5">
                  <FieldLabel htmlFor="combo-price" tipKey="combo.price">Precio final</FieldLabel>
                  <Input
                    id="combo-price"
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.price}
                    onChange={(e) => setForm((f) => ({ ...f, price: parseFloat(e.target.value) || 0 }))}
                    disabled={saving}
                  />
                </div>
              ) : (
                <div className="grid gap-1.5">
                  <FieldLabel htmlFor="combo-discount" tipKey="combo.discountPct">Descuento (%)</FieldLabel>
                  <Input
                    id="combo-discount"
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={form.discountPct}
                    onChange={(e) => setForm((f) => ({ ...f, discountPct: e.target.value }))}
                    disabled={saving}
                    placeholder="Ej: 10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Precio final: <strong>{fmt(calcFinalPrice())}</strong>
                  </p>
                </div>
              )}
            </div>

            {/* Orden */}
            <div className="grid gap-1.5">
              <FieldLabel htmlFor="combo-order" tipKey="combo.sortOrder">Orden</FieldLabel>
              <Input
                id="combo-order"
                type="number"
                min={0}
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                disabled={saving}
              />
            </div>
          </div>
        </CrudEditorShell>
      </ClinicalPageShell>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar combo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.name}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void handleDelete()}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
